/**
 * @todo Quand on aura dev un editeur de contenu, on pourra supprimer ce fichier.
 * Réécriture temporaire des href legacy (ancien site) dans le HTML éditorial.
 * Supprimer ce fichier et son import quand le CMS / les redirects ne seront plus nécessaires.
 */

const SITE_ORIGIN_PATTERN = 'https?:\\/\\/(?:www\\.)?prepeers\\.co'

/** Cible des anciennes URLs `/orientation/*` (aligné sur `nuxt.config` routeRules). */
export const LEGACY_ORIENTATION_TARGET = '/types-ecoles'

/** URL tronquée `/nos-co` (CMS) → hub Nos conseils. */
export const LEGACY_NOS_CO_TARGET = '/nos-conseils'

export interface RewriteLegacyContentLinksOptions {
  /** Rubrique Nos conseils courante : `/nos-conseils/{category}/…`. */
  nosConseilsCategory?: string | null
}

/**
 * Réécrit les liens legacy dans un fragment HTML.
 * @param {string} html - HTML (souvent déjà passé par `normalizeQuillHtml`).
 * @param {RewriteLegacyContentLinksOptions} [options] - Contexte de la page affichée.
 * @returns {string} HTML avec href corrigés.
 */
export function rewriteLegacyContentLinks(
  html: string,
  options: RewriteLegacyContentLinksOptions = {},
): string {
  if (!html) return html

  let result = html
  result = rewriteInscription(result)
  result = rewriteNosCo(result)
  result = rewriteEtablissement(result)
  result = rewriteOrientation(result)
  result = rewriteEditionArticle(result, options.nosConseilsCategory?.trim() ?? null)
  return result
}

/**
 * Chemin attendu après rewrite (pour tests / outils SEO).
 * @param {string} legacyPath - Chemin legacy, ex. `/orientation/niveau-bac-plus-3`.
 * @param {string} [parentPath] - Chemin de la page parente, ex. `/nos-conseils/.../article`.
 * @returns {string | null} Cible attendue ou `null` si règle inconnue.
 */
export function getExpectedLegacyHrefAfterRewrite(
  legacyPath: string,
  parentPath?: string,
): string | null {
  const path = legacyPath.replace(/\/$/, '') || legacyPath

  if (path === '/inscription' || path.startsWith('/inscription/')) {
    return '/register'
  }
  if (path.startsWith('/etablissement/')) {
    return path.replace(/^\/etablissement\//, '/ecoles/')
  }
  if (path === '/orientation' || path.startsWith('/orientation/')) {
    return LEGACY_ORIENTATION_TARGET
  }
  if (path === '/nos-co') {
    return LEGACY_NOS_CO_TARGET
  }
  if (path.startsWith('/edition-article/') && parentPath) {
    const category = nosConseilsCategoryFromPath(parentPath)
    if (category) {
      const slug = path.slice('/edition-article/'.length)
      return `/nos-conseils/${category}/${slug}`
    }
  }
  return null
}

/**
 * @param {string} parentPath - URL ou chemin de la page parente.
 * @returns {string | null} Slug catégorie sous `nos-conseils`.
 */
export function nosConseilsCategoryFromPath(parentPath: string): string | null {
  const segments = parentPath.split('/').filter(Boolean)
  const nosIndex = segments.indexOf('nos-conseils')
  const category = nosIndex === -1 ? undefined : segments[nosIndex + 1]
  return category ?? null
}

/**
 * @param {string | string[] | undefined} slugParam - `route.params.slug` sur une page Nos conseils.
 * @returns {string | undefined} Premier segment (catégorie).
 */
export function nosConseilsCategoryFromRouteSlug(
  slugParam: string | string[] | undefined,
): string | undefined {
  const segments = Array.isArray(slugParam) ? slugParam : slugParam ? [slugParam] : []
  return segments[0] || undefined
}

/**
 * @param {string} html - Fragment HTML.
 * @returns {string} HTML avec `/inscription` → `/register`.
 */
function rewriteInscription(html: string): string {
  let result = html
  result = result.replace(new RegExp(`${SITE_ORIGIN_PATTERN}/inscription/?`, 'gi'), '/register')
  result = result.replace(/(?<=["'\s=])\/inscription\/?(?=["'\s#?]|$)/gi, '/register')
  return result
}

/**
 * @param {string} html - Fragment HTML.
 * @returns {string} HTML avec `/nos-co` → `/nos-conseils`.
 */
function rewriteNosCo(html: string): string {
  let result = html
  result = result.replace(
    new RegExp(`${SITE_ORIGIN_PATTERN}/nos-co(?=["'\\s#?]|$)`, 'gi'),
    LEGACY_NOS_CO_TARGET,
  )
  result = result.replace(/(?<=["'\s=])\/nos-co(?=["'\s#?]|$)/gi, LEGACY_NOS_CO_TARGET)
  return result
}

/**
 * @param {string} html - Fragment HTML.
 * @returns {string} HTML avec `/etablissement/…` → `/ecoles/…`.
 */
function rewriteEtablissement(html: string): string {
  let result = html
  result = result.replace(
    new RegExp(`${SITE_ORIGIN_PATTERN}/etablissement/([^"'\\s<#?]+)`, 'gi'),
    '/ecoles/$1',
  )
  result = result.replace(/(?<=["'\s=])\/etablissement\/([^"'\\s<#?]+)/gi, '/ecoles/$1')
  return result
}

/**
 * @param {string} html - Fragment HTML.
 * @returns {string} HTML avec `/orientation/*` → `/types-ecoles`.
 */
function rewriteOrientation(html: string): string {
  let result = html
  result = result.replace(
    new RegExp(`${SITE_ORIGIN_PATTERN}/orientation(?:/[^"'\\s<#?]*)?`, 'gi'),
    LEGACY_ORIENTATION_TARGET,
  )
  result = result.replace(
    /(?<=["'\s=])\/orientation(?:\/[^"'\\s<#?]*)?/gi,
    LEGACY_ORIENTATION_TARGET,
  )
  return result
}

/**
 * @param {string} html - Fragment HTML.
 * @param {string | null} category - Rubrique Nos conseils de la page courante.
 * @returns {string} HTML avec `/edition-article/{slug}` → `/nos-conseils/{category}/{slug}`.
 */
function rewriteEditionArticle(html: string, category: string | null): string {
  if (!category) return html

  const prefix = `/nos-conseils/${category}`
  let result = html
  result = result.replace(
    new RegExp(`${SITE_ORIGIN_PATTERN}/edition-article/([^"'\\s<#?]+)`, 'gi'),
    `${prefix}/$1`,
  )
  result = result.replace(/(?<=["'\s=])\/edition-article\/([^"'\\s<#?]+)/gi, `${prefix}/$1`)
  return result
}
